import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAlerts } from './useAlerts'
import { useRegulatoryEvents } from './useRegulatoryCalendar'

const LAST_SEEN_ALERTS_KEY = 'torre_last_seen_alerts_ts'
const LAST_SEEN_CALENDAR_KEY = 'torre_last_seen_calendar_ts'

export function useUnseenNotifications() {
  const location = useLocation()

  const [lastSeenAlerts, setLastSeenAlerts] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(LAST_SEEN_ALERTS_KEY)
      if (stored) return parseInt(stored, 10)
      // First visit: initialize to current timestamp
      const now = Date.now()
      localStorage.setItem(LAST_SEEN_ALERTS_KEY, String(now))
      return now
    } catch {
      return Date.now()
    }
  })

  const [lastSeenCalendar, setLastSeenCalendar] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(LAST_SEEN_CALENDAR_KEY)
      if (stored) return parseInt(stored, 10)
      const now = Date.now()
      localStorage.setItem(LAST_SEEN_CALENDAR_KEY, String(now))
      return now
    } catch {
      return Date.now()
    }
  })

  // Mark alerts as seen when on /alerts
  useEffect(() => {
    if (location.pathname.startsWith('/alerts')) {
      const now = Date.now()
      setLastSeenAlerts(now)
      try {
        localStorage.setItem(LAST_SEEN_ALERTS_KEY, String(now))
      } catch {}
    }
  }, [location.pathname])

  // Mark calendar as seen when on /calendar
  useEffect(() => {
    if (location.pathname.startsWith('/calendar')) {
      const now = Date.now()
      setLastSeenCalendar(now)
      try {
        localStorage.setItem(LAST_SEEN_CALENDAR_KEY, String(now))
      } catch {}
    }
  }, [location.pathname])

  const markAlertsAsSeen = useCallback(() => {
    const now = Date.now()
    setLastSeenAlerts(now)
    try {
      localStorage.setItem(LAST_SEEN_ALERTS_KEY, String(now))
    } catch {}
  }, [])

  const markCalendarAsSeen = useCallback(() => {
    const now = Date.now()
    setLastSeenCalendar(now)
    try {
      localStorage.setItem(LAST_SEEN_CALENDAR_KEY, String(now))
    } catch {}
  }, [])

  // Check new alerts arrived after lastSeenAlerts
  const { data: alertsData } = useAlerts({ limit: 50 })
  const unseenAlertsCount = location.pathname.startsWith('/alerts')
    ? 0
    : (alertsData?.data || []).filter((a: any) => {
        if (a.status === 'RESOLVED') return false
        const t = new Date(a.createdAt).getTime()
        return t > lastSeenAlerts
      }).length

  // Check new calendar events added/updated after lastSeenCalendar
  const { data: eventsData } = useRegulatoryEvents()
  const unseenCalendarCount = location.pathname.startsWith('/calendar')
    ? 0
    : (eventsData || []).filter((e: any) => {
        if (e.computedStatus === 'COMPLETED' || e.computedStatus === 'CANCELLED') return false
        const t = new Date(e.createdAt || e.updatedAt || e.nextDueDate).getTime()
        return t > lastSeenCalendar
      }).length

  return {
    unseenAlertsCount,
    unseenCalendarCount,
    markAlertsAsSeen,
    markCalendarAsSeen,
  }
}
